<template>
  <card-dialog
    v-model="modalVisible"
    max-width="600px"
    :title="$t('edit_profile.dialog.header')"
  >
    <v-card-text
      class="pa-6"
    >
      <div>
        <validation-observer
          ref="passwordValidation"
        >
          <div class="d-flex align-center justify-space-between">
            <subtitle class="mb-2">
              {{ $t('edit_profile.dialog.password.title') }}
            </subtitle>

            <v-btn
              text
              color="primary"
              class="text-none"
              @click="onChangePassword = !onChangePassword"
            >
              {{ $t("edit_profile.dialog.change_password.label") }}
            </v-btn>
          </div>
          <form
            v-if="onChangePassword"
            class="mt-2"
            @submit.prevent="changePassword()"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              vid="currentPassword"
              name="Current Password"
            >
              <v-text-field
                v-model="currentPassword"
                outlined
                dense
                :type="showCurrentPassword ? 'text' : 'password'"
                :label="$t('text_input.user.password.title')"
                :error-messages="errors"
                :append-icon="showCurrentPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"
                @click:append="showCurrentPassword = !showCurrentPassword"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              rules="required|not_same:@currentPassword|password_policy"
              vid="newPassword"
              name="New Password"
            >
              <v-text-field
                v-model="newPassword"
                outlined
                dense
                :type="showNewPassword ? 'text' : 'password'"
                :label="$t('text_input.user.new_password.title')"
                :error-messages="errors"
                :append-icon="showNewPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"
                @click:append="showNewPassword = !showNewPassword"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              rules="required|confirmed:newPassword"
              name="Password Confirmation"
            >
              <v-text-field
                v-model="passwordConfirmation"
                outlined
                dense
                :type="showPasswordConfirmation ? 'text' : 'password'"
                :label="$t('text_input.user.repeat_password.title')"
                :error-messages="errors"
                :append-icon="showPasswordConfirmation ? 'fa fa-eye-slash' : 'fa fa-eye'"
                @click:append="showPasswordConfirmation = !showPasswordConfirmation"
              />
            </validation-provider>

            <div class="d-flex justify-end mt-2">
              <v-btn
                color="primary"
                type="submit"
                :loading="passwordSubmitting"
                class="text-none"
                :disabled="disablePasswordButton"
              >
                {{ $t('edit_profile.dialog.button.save_password_change') }}
              </v-btn>
            </div>
          </form>
        </validation-observer>

        <validation-observer ref="profileValidation">
          <subtitle class="mt-5">
            {{ $t('card_title.personal_info') }}
          </subtitle>

          <div class="text-body-2 v-label mb-2">
            {{ $t('text_input.data.date_of_birth.title') }}
          </div>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <dob-input
              v-model="coachForm.birth_date"
              dense
              :error-messages="errors"
            />
          </validation-provider>

          <v-text-field
            v-model="coachForm.address"
            outlined
            dense
            :label="$t('text_input.data.address.title')"
          />

          <v-text-field
            v-model="coachForm.phone"
            outlined
            dense
            :label="$t('text_input.data.phone_number.title')"
            placeholder="+385 99 876 5432"
          />
          <div>
            <div
              v-if="hasRegularReports"
              class="mb-8 mt-2"
            >
              <subtitle class="mb-4">
                {{ $t('edit_profile.dialog.report.title') }}
              </subtitle>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_daily_morning_report') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_daily_morning_report"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_daily_morning_report}"
              >
                <v-col class="body-2">
                  {{ $t('switch.daily_morning_report_time') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.daily_morning_report_time"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_daily_morning_report"
                    :items="timeItems.slice(10, 48)"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_daily_evening_report') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_daily_evening_report"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_daily_evening_report}"
              >
                <v-col class="body-2">
                  {{ $t('switch.daily_evening_report_time') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.daily_evening_report_time"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_daily_evening_report"
                    :items="timeItems.slice(34, 48)"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_event_reports') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_event_reports"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_event_reports}"
              >
                <v-col class="body-2">
                  {{ $t('dropdown.how_soon_after.title') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.event_report_minutes_after_event"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_event_reports"
                    :items="[ { text: $t('how_soon_settings.after.30_mins'), value: 30 }, { text: $t('how_soon_settings.after.1_hour'), value: 60 }, { text: $t('how_soon_settings.after.1.5_hours'), value: 90 }, { text: $t('how_soon_settings.after.2_hours'), value: 120 }, { text: $t('how_soon_settings.after.3_hours'), value: 180 }, { text: $t('how_soon_settings.after.4_hours'), value: 240 }]"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_weekly_report') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_weekly_report"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_weekly_report}"
              >
                <v-col class="body-2">
                  {{ $t('dropdown.weekly_report_day.title') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.weekly_report_day"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_weekly_report"
                    :items="dayItems"
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_weekly_report}"
              >
                <v-col class="body-2">
                  {{ $t('dropdown.weekly_report_time.title') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.weekly_report_time"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_weekly_report"
                    :items="timeItems.slice(10, 48)"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_monthly_report') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_monthly_report"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_monthly_report}"
              >
                <v-col class="body-2">
                  {{ $t('dropdown.monthly_report_time.title') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.monthly_report_time"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_monthly_report"
                    :items="timeItems.slice(10, 48)"
                    hide-details
                  />
                </v-col>
              </v-row>
            </div>

            <subtitle
              v-if="hasItlModule || hasSmartAssistantModule"
              class="mt-2 mb-4"
            >
              {{ $t('new_coach.notification_settings.title') }}
            </subtitle>

            <v-row
              v-if="hasItlModule"
              align="center"
            >
              <v-col class="body-2">
                {{ $t('switch.receive_new_player_feedback') }}
              </v-col>
              <v-col cols="4">
                <v-switch
                  v-model="coachForm.settings.receive_new_feedback_submitted_notification"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="hasSmartAssistantModule"
              align="center"
            >
              <v-col class="body-2">
                {{ $t("switch.receive_smart_assistant") }}
              </v-col>
              <v-col cols="4">
                <v-switch
                  v-model="coachForm.settings.receive_smart_assistant_notifications"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>

            <div
              v-if="(hasPolarIntegration && polarAuthUrl) || (hasGarminIntegration && garminAuthUrl)"
              class="mt-6"
            >
              <subtitle class="mb-2">
                {{ $t('text_input.integrations.title') }}
              </subtitle>

              <v-row
                v-if="hasPolarIntegration"
                align="center"
              >
                <v-col>
                  <div>Polar One</div>
                  <div
                    v-if="coachForm.polar_id"
                    class="text-caption success--text"
                  >
                    <v-icon
                      left
                      x-small
                    >
                      fa fa-check
                    </v-icon>
                    {{ $t('card_title.connected') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    :href="polarAuthUrl"
                  >
                    {{ coachForm.polar_id ? $t("button.reconnect") : $t("button.connect_account") }}
                  </v-btn>
                </v-col>
              </v-row>

              <v-row
                v-if="hasGarminIntegration"
                align="center"
              >
                <v-col>
                  <div>Garmin Connect</div>
                  <div
                    v-if="coachForm.garmin_id"
                    class="text-caption success--text"
                  >
                    <v-icon
                      left
                      x-small
                    >
                      fa fa-check
                    </v-icon>
                    {{ $t('card_title.connected') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    :href="garminAuthUrl"
                  >
                    {{ coachForm.garmin_id ? $t("button.reconnect"): $t("button.connect_account") }}
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </div>
        </validation-observer>
      </div>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          class="text-none"
          @click="submit()"
        >
          {{ $t('edit_profile.dialog.button.save_changes') }}
        </v-btn>
      </v-card-actions>
    </template>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import { AuthApiService, CoachApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import { cloneDeep } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle'
import DobInput from '@/components/helpers/DobInput'
import dayjs from 'dayjs'
import { getDateTimeFormatForUser } from '@/utils/helpers'

const emptySettings = {
  receive_daily_morning_report: true,
  daily_morning_report_time: '10:00',
  receive_daily_evening_report: true,
  daily_evening_report_time: '22:00',
  receive_weekly_report: true,
  weekly_report_day: 1,
  weekly_report_time: '08:00',
  receive_monthly_report: true,
  monthly_report_time: '08:00',
  receive_event_reports: true,
  event_report_minutes_after_event: 90,
  receive_new_feedback_submitted_notification: true,
  receive_smart_assistant_notifications: true
}

const emptyForm = {
  id: null,
  email: '',
  address: '',
  phone: '',
  dob: {
    day: '',
    month: '',
    year: ''
  },
  birth_date: null,
  settings: emptySettings
}

export default {
  name: 'ProfileFormDialog',
  components: { DobInput, Subtitle, CardDialog },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      coachForm: cloneDeep(emptyForm),
      datePickerMenuVisible: false,
      submitting: false,
      passwordSubmitting: false,
      timeItems: [],
      dayItems: [],
      garminAuthUrl: null,
      onChangePassword: false,
      currentPassword: '',
      newPassword: '',
      passwordConfirmation: '',
      showCurrentPassword: false,
      showNewPassword: false,
      showPasswordConfirmation: false
    }
  },
  computed: {
    disablePasswordButton() {
      return (
        !this.currentPassword ||
        this.newPassword !== this.passwordConfirmation
      )
    },
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    polarAuthUrl() {
      if (!this.coachForm.polar_authorize_url) return undefined

      const state = JSON.stringify({
        coach_id: this.coachForm.id,
        redirect_uri: window.location.href
      })

      return this.coachForm.polar_authorize_url + '&state=' + btoa(state)
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasPolarIntegration() {
      return this.user.organisation_settings.integrations.some(i => i.key === 'polar')
    },
    hasGarminIntegration() {
      return this.user.organisation_settings.integrations.some(i => i.key === 'garmin')
    },
    hasRegularReports() {
      return this.user.organisation_settings.has_regular_reports
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module
    },
    hasSmartAssistantModule() {
      return this.user.organisation_settings.has_smart_assistant_module
    }
  },
  watch: {
    visible(value) {
      if (!value) return

      this.fetchGarminAuthUrl()
    }
  },
  created() {
    const items = []

    let startTime = dayjs().startOf('day')
    const day = startTime.day()

    while (startTime.day() === day) {
      items.push({
        value: startTime.format('HH:mm'),
        text: startTime.format(getDateTimeFormatForUser('HH:mm'))
      })
      startTime = startTime.add(30, 'm')
    }

    this.timeItems = items

    for (let d = 1; d <= 3; d++) {
      this.dayItems.push({ value: d, text: dayjs().day(d).format('dddd') })
    }

    EventBus.$on(this.$events.EDIT_PROFILE, async (coach_id) => {
        try {
          const response = await CoachApiService.getCoachDetails(coach_id)
          const detailedCoach = response.data
          this.coachForm = { ...cloneDeep(emptyForm), ...cloneDeep(detailedCoach) }
          this.coachForm.settings = detailedCoach.settings || cloneDeep(emptySettings)
          this.currentPassword = ''
          this.newPassword = ''
          this.passwordConfirmation = ''
          this.onChangePassword = false
          this.modalVisible = true
        } catch (e) {
        }
    })
  },
  methods: {
    async changePassword() {
      this.passwordSubmitting = true
      if (!(await this.$refs.passwordValidation.validate())) {
        this.passwordSubmitting = false
        return
      }

      await this.resetPassword()
    },
    async submit() {
      this.submitting = true

      if (!(await this.$refs.profileValidation.validate())) {
        this.submitting = false
        return
      }

      await this.save()
    },
    async resetPassword() {
      try {
        const data = {
          current_password: this.currentPassword,
          password: this.newPassword,
          password_confirmation: this.passwordConfirmation
        }

        const response = await AuthApiService.changePassword(data)
        const success = response.success

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: success
            ? this.$t('alert.coach_update_password.success')
            : this.$t('alert.coach_update_password.error'),
          color: success ? 'success' : 'error'
        })
        this.currentPassword = ''
        this.newPassword = ''
        this.passwordConfirmation = ''
        this.onChangePassword = false
      } catch (e) {
        let wrong_password = false
        if (e.toString().includes('422')) wrong_password = true
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: wrong_password ? this.$t('alert.coach_update_password_wrong.error') : this.$t('alert.coach_update_password.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.passwordSubmitting = false
      }
    },
    async save() {
      try {
        const data = {
          id: this.coachForm.id,
          birth_date: this.coachForm.birth_date,
          address: this.coachForm.address,
          phone: this.coachForm.phone,
          settings: this.coachForm.settings
        }

        const response = await CoachApiService.editProfile(data)

        if (this.coachForm.id) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.coach_self_update.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.COACH_UPDATED, response.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_update.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async fetchGarminAuthUrl() {
      if (!this.hasGarminIntegration) return

      try {
        const response = await AuthApiService.getGarminAuthUrl({
          coach_id: this.coachForm.id,
          redirect_uri: window.location.href
        })

        this.garminAuthUrl = response.url
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_garmin.error'),
          color: 'error'
        })
        throw e
      }
    }
  }
}
</script>
